import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { LIBELLE_MOTIF_POSTE, type OptionPoste } from '@afs/shared'
import { Bandeau } from '../../../shared/ui/Bandeau.js'
import { Bouton } from '../../../shared/ui/Bouton.js'
import { EtatVide } from '../../../shared/ui/EtatVide.js'
import { estErreurApi } from '../../../shared/api/erreurs.js'
import { declarerSonPoste, postesDuJour } from '../api.js'
import { useOuvrirSession } from '../session.js'
import styles from './Panneau.module.css'

interface EtatNavigation {
  readonly jetonTemporaire?: string
}

/**
 * Ce qui s'affiche à droite du nom d'un site : pourquoi on ne peut pas y
 * prendre son poste, ou qu'il est le site habituel.
 */
function mention(option: OptionPoste): string | null {
  if (option.motif !== null) return LIBELLE_MOTIF_POSTE[option.motif]
  return option.habituel ? 'Votre site habituel' : null
}

/**
 * Où travaillez-vous aujourd'hui ? — ADR 0002.
 *
 * Le site cesse d'être un attribut de compte pour devenir un attribut de
 * session. Avant cet écran, un agent qui allait travailler à l'antenne le
 * mardi enregistrait ses passages sous le site de son agence : les
 * statistiques par site étaient fausses, et l'audit disait qu'un acte avait
 * eu lieu là où il n'avait pas eu lieu.
 *
 * L'étape arrive **après** le mot de passe et le second facteur, jamais
 * avant : tant que l'identité n'est pas établie, il n'y a rien à choisir.
 *
 * Onze entrées ne se cherchent pas, elles se lisent — ni champ de recherche,
 * ni menu déroulant qui cacherait la moitié de la liste. Le site habituel
 * est présélectionné, et `Entrée` suffit.
 */
export function ChoixPoste(): React.JSX.Element {
  const naviguer = useNavigate()
  const emplacement = useLocation()
  const ouvrirSession = useOuvrirSession()
  const [choisi, setChoisi] = useState<string | null>(null)

  const jetonTemporaire = (emplacement.state as EtatNavigation | null)?.jetonTemporaire

  const postes = useQuery({
    queryKey: ['postes-du-jour'],
    queryFn: () => postesDuJour(jetonTemporaire ?? ''),
    enabled: jetonTemporaire !== undefined,
    // Le référentiel ne bouge pas pendant qu'on choisit, et le jeton d'étape
    // ne vit que quelques minutes : rejouer la requête n'apporterait rien.
    staleTime: Infinity,
  })

  const declaration = useMutation({
    mutationFn: (siteId: string) => declarerSonPoste(siteId, jetonTemporaire ?? ''),
    onSuccess: (resultat) => {
      if (resultat.statut !== 'session') return
      ouvrirSession(resultat)
      void naviguer('/', { replace: true })
    },
  })

  // « Entrée valide le site présélectionné » : encore faut-il que le focus
  // soit dans le formulaire. Il se pose sur le site coché dès que la liste
  // arrive — sans quoi Entrée ne faisait rien.
  const listePostes = useRef<HTMLFieldSetElement>(null)
  const listeChargee = postes.data !== undefined
  useEffect(() => {
    if (listeChargee) listePostes.current?.querySelector<HTMLInputElement>('input:checked')?.focus()
  }, [listeChargee])

  // Arrivée directe sur l'URL, ou rechargement : il n'y a pas de jeton, donc
  // rien à déclarer. On repart de la connexion plutôt que d'afficher une
  // liste vide qui ressemblerait à une panne.
  if (jetonTemporaire === undefined) return <Navigate to="/connexion" replace />

  const options = postes.data ?? []
  // Présélection : le site de rattachement, sinon le premier choisissable.
  // Sans repli, un agent dont le site habituel a fermé arriverait sur un
  // écran où rien n'est coché et où le bouton ne fait rien.
  const parDefaut =
    options.find((o) => o.habituel && o.choisissable)?.siteId ??
    options.find((o) => o.choisissable)?.siteId ??
    null
  const selection = choisi ?? parDefaut
  const aucunPosteOuvert = options.length > 0 && options.every((o) => !o.choisissable)
  const choisissables = options.filter((o) => o.choisissable)
  const indisponibles = options.filter((o) => !o.choisissable)

  return (
    <div className={styles.ecran}>
      <form
        className={`${styles.carte} ${styles.carteLarge}`}
        onSubmit={(evenement) => {
          evenement.preventDefault()
          if (selection !== null) declaration.mutate(selection)
        }}
      >
        <div className={styles.entete}>
          <span className={styles.etapeParcours}>
            Après le mot de passe, avant la file d’attente
          </span>
          <h1 className={styles.titre}>Où travaillez-vous aujourd’hui&nbsp;?</h1>
          <p className={styles.sousTitre}>
            Votre poste du jour décide de la file d’attente que vous voyez et du site inscrit sur
            chaque acte que vous enregistrez.
          </p>
        </div>

        {postes.error && (
          <Bandeau
            ton="erreur"
            reference={estErreurApi(postes.error) ? postes.error.requestId : undefined}
          >
            {postes.error instanceof Error
              ? postes.error.message
              : 'Chargement des sites impossible.'}
          </Bandeau>
        )}

        {declaration.error && (
          <Bandeau
            ton="erreur"
            reference={estErreurApi(declaration.error) ? declaration.error.requestId : undefined}
          >
            {declaration.error instanceof Error
              ? declaration.error.message
              : 'Impossible de prendre votre poste.'}
          </Bandeau>
        )}

        {postes.isPending && <p className={styles.mention}>Chargement des sites…</p>}

        {aucunPosteOuvert && (
          <EtatVide
            titre="Aucun site ne vous accueille aujourd’hui"
            explication="Les sites de votre zone sont fermés, ou n’hébergent pas votre poste. Prévenez votre responsable de site : c’est une dotation à corriger, pas une panne."
          />
        )}

        {options.length > 0 && (
          <fieldset ref={listePostes} className={styles.postes}>
            <legend className={styles.mention}>
              {choisissables.length > 1 ? 'Sites où vous pouvez travailler' : 'Votre site'}
            </legend>
            {choisissables.map((option) => (
              <LigneDePoste
                key={option.siteId}
                option={option}
                choisie={selection === option.siteId}
                desactivee={declaration.isPending}
                onChoisir={() => {
                  setChoisi(option.siteId)
                }}
              />
            ))}
          </fieldset>
        )}

        {/* Les autres sites de la zone se consultent, repliés : quinze options
            grisées pour une seule choisissable noyaient le seul choix utile. */}
        {indisponibles.length > 0 && (
          <details className={styles.mention}>
            <summary>
              Autres sites de votre zone ({indisponibles.length}) — ils n’hébergent pas votre poste
              aujourd’hui
            </summary>
            <div className={styles.postes}>
              {indisponibles.map((option) => (
                <LigneDePoste key={option.siteId} option={option} choisie={false} desactivee />
              ))}
            </div>
          </details>
        )}

        <p className={styles.mention}>
          Vous verrez la file d’attente et les patients du site choisi. Pour en changer, il faudra
          vous reconnecter.
        </p>

        <Bouton
          type="submit"
          pleineLargeur
          enCours={declaration.isPending}
          disabled={selection === null}
        >
          Prendre mon poste
        </Bouton>

        <p className={styles.mention}>Entrée valide le site présélectionné.</p>
      </form>
    </div>
  )
}

function LigneDePoste({
  option,
  choisie,
  desactivee,
  onChoisir,
}: {
  readonly option: OptionPoste
  readonly choisie: boolean
  readonly desactivee: boolean
  readonly onChoisir?: () => void
}): React.JSX.Element {
  return (
    <label
      className={`${styles.poste} ${choisie ? styles.posteChoisi : ''} ${
        option.choisissable ? '' : styles.posteIndisponible
      }`.trim()}
    >
      <input
        type="radio"
        name="poste"
        value={option.siteId}
        checked={choisie}
        disabled={!option.choisissable || desactivee}
        onChange={onChoisir}
      />
      <span className={styles.posteIdentite}>
        <span className={styles.posteNom}>{option.nom}</span>
        <span className={styles.posteReference}>{option.code}</span>
      </span>
      {/* La mention porte le texte, jamais la seule couleur : le motif se lit
          en noir et blanc (skill `design-system-afs`). */}
      {mention(option) !== null && <span className={styles.posteMention}>{mention(option)}</span>}
    </label>
  )
}
